import { superAdminApi } from "../authService";
import {
  ApiResponse,
  PaginatedApiResponse,
  PaginationParams,
} from "../../types/api";

const RESOURCE = "/super-admin/cs/nurture";

export type NurtureCampaignKind = "early_drip" | "weekly_gap";
export type NurtureChannel = "email" | "expo_push" | "in_app";

export interface NurtureCampaignCopy {
  subject: string;
  subject_b?: string;
  body: string;
  cta_label: string;
  push_title?: string;
  push_body?: string;
}

export interface NurtureCampaign {
  id: number;
  slug: string;
  kind: NurtureCampaignKind;
  name: string | null;
  target_gap: string;
  offset_hours: number | null;
  priority: number;
  benefit_key: string | null;
  deep_link: string;
  deep_link_expo: string | null;
  channels: NurtureChannel[];
  copy_pt_br: NurtureCampaignCopy;
  copy_en: NurtureCampaignCopy;
  copy_es: NurtureCampaignCopy;
  is_active: boolean;
  stop_on_aha: boolean;
  deliveries_count?: number;
  created_at: string;
  updated_at: string;
}

export interface CsNurtureSettings {
  early_drip_enabled: boolean;
  weekly_nurture_enabled: boolean;
  expo_push_enabled: boolean;
  early_dispatch_hour_utc: number;
  weekly_dispatch_hour_utc: number;
  weekly_dispatch_weekday: number;
  max_early_per_tenant_per_week: number;
  cooldown_days: number;
  stop_early_on_aha: boolean;
  quiet_hours_enabled: boolean;
  quiet_hours_start_local: number;
  quiet_hours_end_local: number;
  at_risk_inactive_hours: number;
  updated_at: string | null;
}

export interface CsAtRiskTenant {
  tenant_id: number;
  tenant_name: string;
  owner_email: string | null;
  owner_name: string | null;
  trial_started_at: string | null;
  hours_since_trial: number | null;
  last_login_at: string | null;
  inactive_hours: number | null;
  early_deliveries_sent: number;
  nurture_opt_out: boolean;
  reason: "never_logged_in" | "inactive";
}

export interface NurtureCampaignPreview {
  subject: string;
  variant: "A" | "B";
  locale: string;
  html: string;
}

export interface NurtureDeliveryRow {
  id: number;
  tenant_id: number;
  tenant_name: string | null;
  campaign_id: number | null;
  campaign_slug: string | null;
  campaign_name: string | null;
  campaign_kind: string | null;
  channel: string;
  sent_at: string | null;
  skipped_reason: string | null;
  meta?: { subject_variant?: string };
  created_at: string;
}

export type UpsertNurtureCampaignPayload = {
  slug: string;
  kind: NurtureCampaignKind;
  name: string;
  target_gap: string;
  offset_hours?: number | null;
  priority: number;
  benefit_key?: string | null;
  deep_link: string;
  deep_link_expo?: string | null;
  channels: NurtureChannel[];
  copy_pt_br: NurtureCampaignCopy;
  copy_en: NurtureCampaignCopy;
  copy_es: NurtureCampaignCopy;
  is_active?: boolean;
  stop_on_aha?: boolean;
};

export const getCsNurtureSettings = async (): Promise<
  ApiResponse<CsNurtureSettings>
> => {
  const response = await superAdminApi.get<ApiResponse<CsNurtureSettings>>(
    `${RESOURCE}/settings`
  );
  return response.data;
};

export const updateCsNurtureSettings = async (
  payload: Partial<CsNurtureSettings>
): Promise<ApiResponse<CsNurtureSettings>> => {
  const response = await superAdminApi.put<ApiResponse<CsNurtureSettings>>(
    `${RESOURCE}/settings`,
    payload
  );
  return response.data;
};

export const getNurtureCampaigns = async (params?: {
  kind?: NurtureCampaignKind;
  is_active?: boolean;
}): Promise<ApiResponse<NurtureCampaign[]>> => {
  const response = await superAdminApi.get<ApiResponse<NurtureCampaign[]>>(
    `${RESOURCE}/campaigns`,
    { params }
  );
  return response.data;
};

export const getNurtureCampaign = async (
  id: number
): Promise<ApiResponse<NurtureCampaign>> => {
  const response = await superAdminApi.get<ApiResponse<NurtureCampaign>>(
    `${RESOURCE}/campaigns/${id}`
  );
  return response.data;
};

export const createNurtureCampaign = async (
  payload: UpsertNurtureCampaignPayload
): Promise<ApiResponse<NurtureCampaign>> => {
  const response = await superAdminApi.post<ApiResponse<NurtureCampaign>>(
    `${RESOURCE}/campaigns`,
    payload
  );
  return response.data;
};

export const updateNurtureCampaign = async (
  id: number,
  payload: Partial<UpsertNurtureCampaignPayload>
): Promise<ApiResponse<NurtureCampaign>> => {
  const response = await superAdminApi.patch<ApiResponse<NurtureCampaign>>(
    `${RESOURCE}/campaigns/${id}`,
    payload
  );
  return response.data;
};

export const getCsAtRiskTenants = async (params?: {
  limit?: number;
}): Promise<ApiResponse<CsAtRiskTenant[]>> => {
  const response = await superAdminApi.get<ApiResponse<CsAtRiskTenant[]>>(
    `${RESOURCE}/at-risk`,
    { params }
  );
  return response.data;
};

export const previewNurtureCampaign = async (
  id: number,
  payload?: {
    locale?: "pt-BR" | "en" | "es";
    variant?: "A" | "B";
    tenant_name?: string;
    user_name?: string;
  }
): Promise<ApiResponse<NurtureCampaignPreview>> => {
  const response = await superAdminApi.post<ApiResponse<NurtureCampaignPreview>>(
    `${RESOURCE}/campaigns/${id}/preview`,
    payload ?? {}
  );
  return response.data;
};

export const getNurtureDeliveries = async (
  params?: PaginationParams & { tenant_id?: number; campaign_id?: number }
): Promise<PaginatedApiResponse<NurtureDeliveryRow>> => {
  const response = await superAdminApi.get<
    PaginatedApiResponse<NurtureDeliveryRow>
  >(`${RESOURCE}/deliveries`, { params });
  return response.data;
};

export const updateTenantNurtureOptOut = async (
  tenantId: number,
  nurture_opt_out: boolean
): Promise<
  ApiResponse<{
    tenant_id: number;
    nurture_opt_out: boolean;
    nurture_opted_out_at: string | null;
  }>
> => {
  const response = await superAdminApi.patch<
    ApiResponse<{
      tenant_id: number;
      nurture_opt_out: boolean;
      nurture_opted_out_at: string | null;
    }>
  >(`/super-admin/tenants/${tenantId}/nurture-opt-out`, { nurture_opt_out });
  return response.data;
};
